<!-- eslint-disable max-len -->
<template lang="pug">
app-main-content-layout(title='')
  .c-requests-page
    //- Header Section
    section.c-requests-page__header
      .c-requests-page__header-content
        h1.c-requests-page__heading Requests
        p.c-requests-page__subtitle Manage organization claim requests

    //- Table Section
    section.c-requests-table-section
      CmsListToolbar(
        :tabs="viewTabs"
        :active-tab="currentView"
        v-model="searchQuery"
        search-placeholder="Search requests by name, email, or page..."
        :sort-value="sortBy"
        :sort-options="sortOptions"
        :selected-count="selected.length"
        @tab-change="handleTabChange"
        @search="searchFilter"
        @update:sortValue="sortBy = $event"
        @sort="sortTable"
        @clear-selection="tableRef?.clearSelection()"
      )
        template(#bulk)
          el-button.c-requests-bulk-button(type="primary" @click="bulkUpdate('Approved')")
            fai(type="fal" icon="check")
            span Approve
          el-button.c-requests-bulk-button(type="danger" @click="bulkUpdate('Declined')")
            fai(type="fal" icon="times")
            span Decline

      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--checkbox
            .c-skeleton-cell.c-skeleton-cell--name
            .c-skeleton-cell.c-skeleton-cell--email
            .c-skeleton-cell.c-skeleton-cell--page
            .c-skeleton-cell.c-skeleton-cell--ein
            .c-skeleton-cell.c-skeleton-cell--status
      
      //- Table
      el-table(
        v-else
        ref="tableRef"
        :data="items.value"
        @row-click="routeToOrg($event)"
        style="width: 100%"
        @selection-change="onCheckboxChange"
      ).clickable-rows
        el-table-column(width="15")
        el-table-column(type="selection" width="55")
        el-table-column(label="Name" min-width="156")
          template(#default="scope")
            .lh-sm(v-if="scope.row.claimer")
              .font-black {{ scope.row.claimer.firstname }} {{ scope.row.claimer.lastname }}
            .lh-sm(v-else)
              .font-black N/A
        
        el-table-column(label="Email" min-width="200")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.email }}

        el-table-column(label="Page" min-width="230")
          template(#default="scope")
            .lh-sm
              .font-medium.text-decoration-underline {{ scope.row.causeName }}

        el-table-column(label="EIN" min-width="120")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.causeId }}
        
        el-table-column(label="Status" min-width="100")
          template(#default="scope")
            span.crm-badge(:class="getBadgeClass(scope.row.claimStatus)") {{ scope.row.claimStatus }}

        el-table-column(width="46")
          template(#default)
            .el-table__expand-icon
              img(src="@/assets/icons/light/chevron-right-light.svg")

        el-table-column(width="15")

      //- Pagination
      .c-requests-pagination
        el-pagination(
          v-model:current-page="page"
          layout="prev, pager, next"
          :total="totalCount"
          :page-size="pageSize"
          @size-change="handlePageSizeChange"
          @current-change="handleCurrentPageChange"
        )
</template>

<script lang="ts">
import {
  defineComponent, ref, onMounted, reactive,
} from 'vue';
import CmsListToolbar from '@/components/CmsListToolbar.vue';
import shared from '../shared';

export default defineComponent({
  name: 'ManageBenevityRequests',
  components: { CmsListToolbar },
  setup() {
    const {
      router,
      loading,
      getSyncedBenevityCauses,
      mutateBulkClaimStatus,
    } = shared();
    const items = reactive([]);
    const serverError = ref('');

    const currentView = ref('All');
    const selected = ref([]);
    const tableRef = ref();
    const viewTabs = ['All', 'Pending', 'Approved', 'Declined']
      .map((tab) => ({ label: tab, value: tab }));
    const searchQuery = ref('');
    const sortOptions = [
      { label: 'EIN', value: 'ein' },
      { label: 'Page', value: 'causeName' },
    ];
    const sortBy = ref('');

    const query = ref(null);
    const claimStatus = ref(null);
    const page = ref(1);
    const pageSize = ref(10);
    const totalCount = ref(0);

    // Track the latest request to prevent race conditions
    let latestRequestId = 0;

    function routeToOrg(itm) {
      router.push(`/manage/benevity-claim/${itm.id}`);
    }

    function sortTable() {
      if (sortBy.value && sortBy.value.value) {
        items.value = items.value.sort((a, b) => {
          const aVal = a[sortBy.value.value] || '';
          const bVal = b[sortBy.value.value] || '';
          return aVal.toString().localeCompare(bVal.toString());
        });
      }
    }

    function onCheckboxChange(checkedItems) {
      selected.value = checkedItems;
    }

    function getBadgeClass(status) {
      switch (status) {
        case 'Approved':
          return 'crm-badge--teal';
        case 'Pending':
          return 'crm-badge--yellow';
        case 'Declined':
          return 'crm-badge--gray';
        default:
          return '';
      }
    }

    async function findSyncedBenevityCauses() {
      // Increment request ID to track this specific request
      const currentRequestId = ++latestRequestId;
      
      try {
        loading.value = true;
        
        const benevityCausesPaginationInput = {
          query: query.value,
          page: page.value,
          pageSize: pageSize.value,
          claimedEntityId: null,
          claimStatus: claimStatus.value,
        };
        const response = await getSyncedBenevityCauses(benevityCausesPaginationInput);
        
        // Only update if this is still the latest request (prevents race conditions)
        if (currentRequestId === latestRequestId) {
          items.value = response.benevityCauses;
          totalCount.value = response.totalCount;
        }
      } catch (err) {
        console.error('Error fetching requests:', err);
      } finally {
        // Only clear loading if this is still the latest request
        if (currentRequestId === latestRequestId) {
          loading.value = false;
        }
      }
    }

    async function bulkUpdate(type) {
      const bulkCauses = [];
      selected.value.forEach((element) => {
        const item = { id: element.id, status: type };
        bulkCauses.push(item);
      });
      await mutateBulkClaimStatus(bulkCauses);
      selected.value = [];
      await findSyncedBenevityCauses();
    }

    async function handleTabChange(filterBy) {
      currentView.value = filterBy;
      page.value = 1;
      
      // Reset search query and update the search input
      query.value = null;
      searchQuery.value = '';

      if (filterBy === 'Pending') {
        claimStatus.value = 'Pending';
      } else if (filterBy === 'Approved') {
        claimStatus.value = 'Approved';
      } else if (filterBy === 'Declined') {
        claimStatus.value = 'Declined';
      } else {
        claimStatus.value = null;
      }

      await findSyncedBenevityCauses();
    }

    const handlePageSizeChange = (val) => {
      console.log(`${val} items per page`);
    };

    async function handleCurrentPageChange(pageNumber) {
      page.value = pageNumber;
      await findSyncedBenevityCauses();
    }

    async function searchFilter(searchValue: string) {
      // Update query value based on search input
      if (searchValue) {
        const trimmedValue = searchValue.trim();
        if (trimmedValue.length > 2) {
          query.value = trimmedValue;
        } else {
          // Don't search if less than 3 characters
          return;
        }
      } else {
        query.value = null;
      }
      
      // Reset to first page when searching
      page.value = 1;
      await findSyncedBenevityCauses();
    }

    onMounted(async () => {
      await findSyncedBenevityCauses();
    });

    return {
      // data (static)
      loading,
      tableRef,
      viewTabs,

      // data (reactive)
      items,
      totalCount,
      serverError,
      selected,
      searchQuery,
      currentView,
      sortOptions,
      sortBy,
      page,
      pageSize,
      // methods
      getBadgeClass,
      handleTabChange,
      onCheckboxChange,
      bulkUpdate,
      sortTable,
      handlePageSizeChange,
      handleCurrentPageChange,
      routeToOrg,
      searchFilter,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../crm-list';

.c-requests-page {
  @include crm-page;
}

@include crm-header('c-requests-page');
@include crm-table('c-requests-table-section');
@include crm-cells;
@include crm-pagination('c-requests-pagination');
@include crm-skeleton;

.c-skeleton-cell {
  &--page {
    width: 230px;
  }

  &--ein {
    width: 120px;
  }
}

.c-requests-bulk-button.el-button + .c-requests-bulk-button.el-button {
  margin-left: 0;
}

.c-requests-table-section :deep(.el-table__expand-icon) {
  display: flex;
  align-items: center;
  height: 20px;
}

.font-black {
  font-weight: 500;
  color: $foreground;
}

.font-medium {
  color: $gray-600;
}

:deep(.el-checkbox__label) {
  display: none;
}

@media (max-width: 768px) {
  .c-requests-page {
    padding: 1.5rem;
  }
}

@media (max-width: 480px) {
  .c-requests-page {
    padding: 1rem;
  }
}
</style>
